import { CircleInfoIcon } from "@xola/icons";
import { SubmitButton, Tooltip } from "@xola/ui-kit";
import { isEquipmentBased, isScheduled, SwitchField } from "common";
import { produce } from "immer";
import { merge } from "lodash";
import React, { useState } from "react";
import {
    AllowedPrivacy,
    Catalog,
    DemographicPricingGroup,
    Experience,
    FeatureRole,
    LineItemTemplate,
    LineItemTemplateCode,
    PaymentMethod,
    PricingStructure,
    PricingType,
    UnitType,
} from "types";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { hasTerms, mergePricingTypeAndTerms } from "../../../models/DemographicPricingGroup";
import { hasPrivateOption, hasPublicOption, isOptionallyPrivateExperience } from "../../../models/Experience";
import { getLineItemTemplateByCode } from "../../../models/LineItemTemplate";
import { isOnFlexSubscription } from "../../../models/PartnerFeeFormula";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { DemographicCatalogEditor } from "./DemographicCatalogEditor";
import { DownDepositSelector } from "./DownDepositSelector";
import { EquipmentCatalogEditor } from "./EquipmentCatalogEditor";
import { ExperiencePricingFormPayload, getValidationSchema } from "./ExperiencePricingFormHelper";
import { PaymentRequirementSelector } from "./PaymentRequirementSelector";
import { PricingEditor } from "./PricingEditor";
import { ProductPrivacySelector } from "./ProductPrivacySelector";
import { PromotePrivatePricingSelector } from "./PromotePrivatePricingSelector";
import { PurchasableItemTypeSelector } from "./PurchasableItemTypeSelector";
import { SecondaryDemographicCatalogEditor } from "./SecondaryDemographicCatalogEditor";
import { getCatalogItemsByUnitType } from "@/models/Catalog";
import { generateRelativePricingEquipmentPrices } from "@/models/EquipmentPrice";
import {
    generateRelativePricingDemographicPrices,
    getDefaultRelativePricingValues,
} from "@/models/RelativePricingGroup";
import { hasFeature } from "@/models/Seller";

interface ExperiencePricingFormProps {
    isLoading: boolean;
    experience: Experience;
    value: ExperiencePricingFormPayload;
    lineItemTemplates: LineItemTemplate[];
    publicPricingGroup: DemographicPricingGroup;
    privatePricingGroup: DemographicPricingGroup;
    onSubmit: (value: ExperiencePricingFormPayload) => void;
}

export const ExperiencePricingForm = ({
    isLoading,
    experience,
    value,
    lineItemTemplates,
    publicPricingGroup,
    privatePricingGroup,
    onSubmit,
}: ExperiencePricingFormProps) => {
    const seller = useSeller();
    const canSellEquipment =
        hasFeature(seller, FeatureRole.SELLER_INVENTORY) &&
        seller.preferences.inventory.allowEquipmentAsPurchasableUnit;
    const [collectDemographicInfo, setCollectDemographicInfo] = useState(false);
    const fixedPriceLineItemTemplate = getLineItemTemplateByCode(
        lineItemTemplates,
        LineItemTemplateCode.PER_OUTING_PRICE,
    );
    const validationSchema = getValidationSchema(fixedPriceLineItemTemplate!);
    const isExperienceScheduled = isScheduled(experience);

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validationSchema={validationSchema}
            initialValues={value}
            onSubmit={(values, { resetForm }) => {
                onSubmit(values);
                resetForm();
            }}
        >
            {({ values, setFieldValue, setValues, handleSubmit, errors, handleReset, isSubmitting, isValid }) => {
                const catalogItemsError = errors?.catalog?.items as string;
                const equipmentItemsError = (errors?.catalog as any)?.equipmentItems as string | undefined;
                const isEquipment = isEquipmentBased(values);
                const handlePaymentMethodChange = (value: PaymentMethod) => {
                    setFieldValue("paymentMethod", value);
                    if (value === PaymentMethod.LATER) {
                        setFieldValue("downDeposit", null);
                    }
                };

                const handleCreditCardRequiredChange = (value: boolean, sourceValue: string) => {
                    let requireCCForSource = [...(values.requireCCForSources ?? [])];
                    if (value && !requireCCForSource.includes(sourceValue)) {
                        requireCCForSource.push(sourceValue);
                    } else if (!value && requireCCForSource.includes(sourceValue)) {
                        requireCCForSource = requireCCForSource.filter((source) => source !== sourceValue);
                    }

                    setFieldValue("requireCCForSources", requireCCForSource);
                };

                const handlePrivacyChange = (value: AllowedPrivacy[]) => {
                    const updatedValues = produce(values, (draft) => {
                        const currentValue = [...draft.allowedPrivacies];
                        // When Private is changed to public or viceversa. Retaining prices
                        if (currentValue.length === 1 && value.length === 1) {
                            if (value[0] === AllowedPrivacy.PRIVATE) {
                                draft.privatePricing = mergePricingTypeAndTerms(
                                    values.privatePricing,
                                    values.publicPricing,
                                );
                            } else if (
                                value[0] === AllowedPrivacy.PUBLIC &&
                                values.privatePricing.pricingType === PricingType.PER_PERSON
                            ) {
                                draft.publicPricing = mergePricingTypeAndTerms(
                                    values.publicPricing,
                                    values.privatePricing,
                                );
                            }
                        } else if (currentValue.length > 1 && value.length === 1) {
                            if (
                                value[0] === AllowedPrivacy.PRIVATE &&
                                hasTerms(values.publicPricing) &&
                                !hasTerms(values.privatePricing)
                            ) {
                                draft.privatePricing = mergePricingTypeAndTerms(
                                    values.privatePricing,
                                    values.publicPricing,
                                );
                            } else if (
                                value[0] === AllowedPrivacy.PUBLIC &&
                                hasTerms(values.privatePricing) &&
                                !hasTerms(values.publicPricing) &&
                                values.privatePricing.pricingType === PricingType.PER_PERSON
                            ) {
                                draft.publicPricing = mergePricingTypeAndTerms(
                                    values.publicPricing,
                                    values.privatePricing,
                                );
                            }
                        }

                        draft.allowedPrivacies = value;
                    });

                    setValues(updatedValues);
                };

                const handlePurchasableItemTypeChange = (supportedCatalogItemTypes: UnitType[]) => {
                    setFieldValue("supportedCatalogItemTypes", supportedCatalogItemTypes);
                };

                const handleCatalogChange = (value: Catalog) => {
                    setFieldValue("catalog", value);
                    // Incase private pricing has Relative pricing, then add the newly added catalog items to the demographic prices
                    // Copy the values from the first exsting demographic price and save the same values to every demographic price obj
                    // Note: we will run this only if there are more than 0 demographics, else we will lose the current relative pricing as it may override
                    // relative pricing with empty array. since we cannot save with 0 demographics, as soon as we re-add the demographic, the pricing will get re-generated.
                    if (
                        values.privatePricing &&
                        values.privatePricing.pricingStructure === PricingStructure.RELATIVE &&
                        value.items.length > 0
                    ) {
                        // Get the existing first demographicPrice from relative pricing
                        const currentPricingTerm = { ...values.privatePricing.terms?.[0] };
                        const currentDemographicPrice = currentPricingTerm?.demographicPrices?.[0] ?? {};
                        const { adjustmentAmount, adjustmentOperation, adjustmentType } = merge(
                            getDefaultRelativePricingValues(),
                            currentDemographicPrice,
                        );
                        // loop through all the demographics that are there (not removed) and update the values for every demographic
                        const generateRelativePrices = isEquipment
                            ? generateRelativePricingEquipmentPrices
                            : generateRelativePricingDemographicPrices;
                        const updatedDemographicPrices = generateRelativePrices(
                            value.items,
                            adjustmentAmount,
                            adjustmentOperation,
                            adjustmentType,
                        );
                        const updatedPricingTerm = produce(currentPricingTerm, (draft) => {
                            draft.demographicPrices = updatedDemographicPrices;
                        });
                        setFieldValue(`privatePricing.terms`, [updatedPricingTerm]);
                    }
                };

                const hasDemographicSecondary =
                    getCatalogItemsByUnitType(values.catalog.items, UnitType.DEMOGRAPHIC_SECONDARY).length > 0;
                // collectDemographicInfo tracks the toggle being on before any secondary items are added.
                // hasDemographicSecondary ensures the toggle re-syncs on enableReinitialize (e.g. after save).
                const collectInfo = hasDemographicSecondary || collectDemographicInfo;

                const handleCollectDemographicInfoChange = (enabled: boolean) => {
                    setCollectDemographicInfo(enabled);
                    if (!enabled) {
                        const catalog = produce(values.catalog, (draft) => {
                            draft.items = draft.items.filter(
                                (item) => item.unitType !== UnitType.DEMOGRAPHIC_SECONDARY,
                            );
                        });
                        handleCatalogChange(catalog);
                    }
                };

                return (
                    <>
                        <h2 className="mt-5">Pricing</h2>
                        <div className="mb-10 divide-y divide-gray-light" data-testid="experience-pricing-form">
                            {isExperienceScheduled ? (
                                <ContentLayout.Section title="Experience Types">
                                    <ProductPrivacySelector experience={values} onChange={handlePrivacyChange} />
                                </ContentLayout.Section>
                            ) : null}
                            {canSellEquipment ? (
                                <ContentLayout.Section
                                    title="Purchasable Items"
                                    description="Represents either demographics or equipment that can be purchased by customers"
                                >
                                    <PurchasableItemTypeSelector
                                        experience={values}
                                        onChange={handlePurchasableItemTypeChange}
                                    />
                                    {isLoading || isSubmitting ? (
                                        <Loader />
                                    ) : isEquipment ? (
                                        <div className="mt-5 space-y-5">
                                            <EquipmentCatalogEditor
                                                experience={experience}
                                                value={values.catalog}
                                                error={equipmentItemsError}
                                                onChange={handleCatalogChange}
                                            />
                                            <div className="flex items-center">
                                                <SwitchField
                                                    isChecked={collectInfo}
                                                    onChange={handleCollectDemographicInfoChange}
                                                >
                                                    Collect demographic information
                                                </SwitchField>
                                                <Tooltip content="Enable this to collect customer waivers, gather additional information through questionnaires, and access customer analytics">
                                                    <CircleInfoIcon className="ml-1" />
                                                </Tooltip>
                                            </div>
                                            {collectInfo ? (
                                                <SecondaryDemographicCatalogEditor
                                                    experience={experience}
                                                    value={values.catalog}
                                                    error={catalogItemsError}
                                                    onChange={handleCatalogChange}
                                                />
                                            ) : null}
                                        </div>
                                    ) : (
                                        <div className="mt-5">
                                            <DemographicCatalogEditor
                                                experience={experience}
                                                value={values.catalog}
                                                error={catalogItemsError}
                                                onChange={handleCatalogChange}
                                            />
                                        </div>
                                    )}
                                </ContentLayout.Section>
                            ) : (
                                <ContentLayout.Section title="Demographics">
                                    {isLoading || isSubmitting ? (
                                        <Loader />
                                    ) : (
                                        <DemographicCatalogEditor
                                            experience={experience}
                                            value={values.catalog}
                                            error={errors?.catalog?.items as string}
                                            onChange={handleCatalogChange}
                                        />
                                    )}
                                </ContentLayout.Section>
                            )}

                            {hasPublicOption(values) && publicPricingGroup ? (
                                <ContentLayout.Section title={isExperienceScheduled ? "Public Pricing" : "Pricing"}>
                                    {isLoading || isSubmitting ? (
                                        <Loader />
                                    ) : (
                                        <PricingEditor
                                            privacy={AllowedPrivacy.PUBLIC}
                                            pricingGroup={values.publicPricing}
                                            lineItemTemplates={lineItemTemplates}
                                            errors={errors?.publicPricing}
                                            experience={values}
                                            onChange={(key: string, value: any) =>
                                                setFieldValue(`publicPricing.${key}`, value)
                                            }
                                        />
                                    )}
                                </ContentLayout.Section>
                            ) : null}

                            {hasPrivateOption(values) && privatePricingGroup ? (
                                <ContentLayout.Section title="Private Pricing">
                                    {isLoading || isSubmitting ? (
                                        <Loader />
                                    ) : (
                                        <PricingEditor
                                            privacy={AllowedPrivacy.PRIVATE}
                                            pricingGroup={values.privatePricing}
                                            lineItemTemplates={lineItemTemplates}
                                            errors={errors?.privatePricing}
                                            experience={values}
                                            hasTieredPublicPricing={values.publicPricing?.pricingStructure === "tiered"}
                                            onChange={(key: string, value: any) =>
                                                setFieldValue(`privatePricing.${key}`, value)
                                            }
                                        />
                                    )}
                                </ContentLayout.Section>
                            ) : null}

                            <ContentLayout.Section
                                title="Payment Requirement"
                                description="Choose whether payment is required on or not."
                            >
                                <PaymentRequirementSelector
                                    experience={values}
                                    canChangePaymentMethods={!isOnFlexSubscription(seller)}
                                    onPaymentMethodChange={(value) => handlePaymentMethodChange(value)}
                                    onCreditCardRequiredChange={(value, source) =>
                                        handleCreditCardRequiredChange(value, source)
                                    }
                                />
                            </ContentLayout.Section>

                            <ContentLayout.Section title="Down Deposit">
                                <DownDepositSelector
                                    experience={experience}
                                    value={values}
                                    errors={errors}
                                    perPersonLabel={
                                        isEquipment ? "per purchasable equipment" : "per purchasable demographic"
                                    }
                                    onChange={setFieldValue}
                                />
                            </ContentLayout.Section>
                            {isOptionallyPrivateExperience(values) ? (
                                <ContentLayout.Section
                                    title="Promote Private Pricing"
                                    description="Presents your private pricing as an upsell banner instead of an upfront option. We suggest adding motivational content specific to this product to help promote your upsell."
                                >
                                    <PromotePrivatePricingSelector value={values} onChange={setFieldValue} />
                                </ContentLayout.Section>
                            ) : null}
                        </div>
                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton
                                isLoading={isLoading}
                                disabled={!isValid}
                                size="medium"
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                            <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </>
                );
            }}
        </XolaFormik>
    );
};
